import React, { useState } from 'react';
import axios from 'axios';
import { API_BASE_URL } from '../services/api';

const fmt2 = (n) => {
  const v = Number(n);
  if (!isFinite(v) || v === 0) return '';
  return v.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

const fmt4 = (n) => {
  const v = Number(n);
  if (!isFinite(v) || v === 0) return '';
  return v.toLocaleString('en-US', { minimumFractionDigits: 4, maximumFractionDigits: 4 });
};

const fmtDate = (v) => {
  if (!v) return '';
  return String(v).slice(0, 10);
};

const PRODUCT_OPTIONS = [
  { value: 'all', label: 'All Products' },
  { value: 'gsec', label: 'GSec' },
  { value: 'tbill', label: 'T-Bill' },
  { value: 'money_market', label: 'Money Market' }
];

const COLUMNS = [
  { key: 'product_type', label: 'Product' },
  { key: 'isin', label: 'ISIN' },
  { key: 'deal_number', label: 'Deal No.' },
  { key: 'portfolio', label: 'Portfolio' },
  { key: 'face_value', label: 'Face Value', right: true, fn: fmt2 },
  { key: 'coupon_rate', label: 'Rate / Yield %', right: true, fn: fmt4 },
  { key: 'value_date', label: 'Value Date', fn: fmtDate },
  { key: 'maturity_date', label: 'Maturity Date', fn: fmtDate },
  { key: 'accrual_days', label: 'Days in Period', right: true },
  { key: 'daily_accrual', label: 'Daily Accrual', right: true, fn: fmt4 },
  { key: 'period_interest', label: 'Period Interest (LKR)', right: true, fn: fmt2 }
];

const InterestIncomeReportPage = () => {
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [product, setProduct] = useState('all');
  const [data, setData] = useState([]);
  const [summary, setSummary] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleGenerate = async () => {
    if (!startDate || !endDate) {
      setError('Please select both start and end dates.');
      return;
    }
    setLoading(true);
    setError('');
    try {
      const params = new URLSearchParams({ startDate, endDate });
      if (product && product !== 'all') params.set('product', product);
      const res = await axios.get(`${API_BASE_URL}/reports/interest-income?${params}`);
      setData(res.data.data || []);
      setSummary(res.data.summary || []);
    } catch (e) {
      setError(e.response?.data?.error || e.message || 'Error fetching report');
    } finally {
      setLoading(false);
    }
  };

  const totalInterest = summary.reduce((s, r) => s + (Number(r.total_period_interest) || 0), 0);

  return (
    <div className="ui-page" style={{ padding: 24 }}>
      <h2 style={{ marginBottom: 8 }}>Interest Income Breakup</h2>
      <p style={{ color: '#6c757d', marginBottom: 24 }}>
        Shows interest / discount income accrued across GSec, T-Bill and Money Market deals for the selected period.
      </p>

      {/* Filters */}
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 16, alignItems: 'flex-end', marginBottom: 24, padding: 16, background: '#f8f9fa', border: '1px solid #dee2e6', borderRadius: 8 }}>
        <div>
          <label style={{ display: 'block', marginBottom: 4, fontWeight: 500, fontSize: 13 }}>Start Date *</label>
          <input type="date" value={startDate} onChange={e => setStartDate(e.target.value)}
            style={{ padding: '8px 10px', border: '1px solid #ced4da', borderRadius: 4, fontSize: 14 }} />
        </div>
        <div>
          <label style={{ display: 'block', marginBottom: 4, fontWeight: 500, fontSize: 13 }}>End Date *</label>
          <input type="date" value={endDate} onChange={e => setEndDate(e.target.value)}
            style={{ padding: '8px 10px', border: '1px solid #ced4da', borderRadius: 4, fontSize: 14 }} />
        </div>
        <div>
          <label style={{ display: 'block', marginBottom: 4, fontWeight: 500, fontSize: 13 }}>Product</label>
          <select value={product} onChange={e => setProduct(e.target.value)}
            style={{ padding: '8px 10px', border: '1px solid #ced4da', borderRadius: 4, fontSize: 14, minWidth: 160 }}>
            {PRODUCT_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
        </div>
        <button type="button" onClick={handleGenerate} disabled={loading || !startDate || !endDate}
          style={{ padding: '8px 18px', background: '#007bff', color: '#fff', border: 'none', borderRadius: 4, cursor: (loading || !startDate || !endDate) ? 'not-allowed' : 'pointer', opacity: (loading || !startDate || !endDate) ? 0.6 : 1 }}>
          {loading ? 'Generating…' : 'Generate Report'}
        </button>
      </div>

      {error && (
        <div style={{ background: '#fee', border: '1px solid #fcc', borderRadius: 4, padding: '10px 14px', marginBottom: 16, color: '#c00' }}>
          {error}
        </div>
      )}

      {/* Summary by product */}
      {summary.length > 0 && (
        <div style={{ marginBottom: 24 }}>
          <h4 style={{ marginBottom: 10 }}>Summary by Product</h4>
          <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
            {summary.map(s => (
              <div key={s.product_type} style={{ background: '#fff', border: '1px solid #dee2e6', borderRadius: 8, padding: '14px 20px', minWidth: 200 }}>
                <div style={{ fontWeight: 700, fontSize: 15, marginBottom: 6 }}>{s.product_type}</div>
                <div style={{ fontSize: 13, color: '#555' }}>Deals: <strong>{s.deal_count}</strong></div>
                <div style={{ fontSize: 13, color: '#555' }}>Face Value: <strong>{fmt2(s.total_face_value)}</strong></div>
                <div style={{ fontSize: 13, color: '#0066cc', marginTop: 4 }}>Period Interest: <strong>{fmt2(s.total_period_interest)}</strong></div>
              </div>
            ))}
            <div style={{ background: '#e8f4e8', border: '1px solid #b8dbb8', borderRadius: 8, padding: '14px 20px', minWidth: 200 }}>
              <div style={{ fontWeight: 700, fontSize: 15, marginBottom: 6 }}>Grand Total</div>
              <div style={{ fontSize: 14, color: '#006600' }}>Total Interest: <strong>{fmt2(totalInterest)}</strong></div>
            </div>
          </div>
        </div>
      )}

      {/* Detail table */}
      {data.length > 0 && (
        <div style={{ background: '#fff', border: '1px solid #dee2e6', borderRadius: 8, overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead>
              <tr>
                {COLUMNS.map(c => (
                  <th key={c.key} style={{ padding: '10px 12px', background: '#f8f9fa', borderBottom: '2px solid #dee2e6', textAlign: c.right ? 'right' : 'left', whiteSpace: 'nowrap', fontWeight: 600 }}>
                    {c.label}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {data.map((row, i) => (
                <tr key={i} style={{ borderBottom: '1px solid #f0f0f0' }}>
                  {COLUMNS.map(c => (
                    <td key={c.key} style={{ padding: '8px 12px', textAlign: c.right ? 'right' : 'left' }}>
                      {c.fn ? c.fn(row[c.key]) : (row[c.key] != null ? row[c.key] : '—')}
                    </td>
                  ))}
                </tr>
              ))}
              {/* Footer totals row */}
              <tr style={{ background: '#f5f5f5', fontWeight: 700, borderTop: '2px solid #ccc' }}>
                {COLUMNS.map(c => (
                  <td key={c.key} style={{ padding: '10px 12px', textAlign: c.right ? 'right' : 'left' }}>
                    {c.key === 'product_type' ? 'TOTAL' : c.key === 'period_interest' ? fmt2(totalInterest) : ''}
                  </td>
                ))}
              </tr>
            </tbody>
          </table>
        </div>
      )}

      {!loading && data.length === 0 && !error && (
        <div style={{ textAlign: 'center', color: '#888', padding: 40 }}>Select a date range and click Generate Report.</div>
      )}
    </div>
  );
};

export default InterestIncomeReportPage;
